import { Component, OnInit, AfterViewInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { RoutinesService } from "../../services/routines.service";
import { AuthService } from "src/app/services/auth.service";
import { ErrorHandlerService } from "src/app/services/error-handler.service";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatSort, Sort } from "@angular/material/sort";
import Swal from "sweetalert2";

interface RoutineResponse {
  id: string;
  name: string;
  responsibility?: { name: string };
}

@Component({
  selector: "app-routines-list",
  templateUrl: "./routines-list.component.html",
  styleUrls: ["./routines-list.component.css"],
})
export class RoutinesListComponent implements OnInit, AfterViewInit {
  displayedColumns: string[] = ["name", "responsibilityName", "actions"];
  dataSource = new MatTableDataSource<RoutineResponse>([]);
  loading = false;

  currentPage = 0;
  pageSize = 10;
  totalElements = 0;
  sortField = "name";
  sortDirection = "asc";
  serverSide = false;

  isAdmin = false;
  isManager = false;
  isSupervisor = false;
  isUser = false;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private authService: AuthService,
    private routinesService: RoutinesService,
    private toast: ToastrService,
    public router: Router,
    private errorHandlerService: ErrorHandlerService,
  ) {}

  ngOnInit(): void {
    this.authService.getUserRoles().forEach((role) => {
      switch (role.name) {
        case "ROLE_ADMIN":
          this.isAdmin = true;
          break;
        case "ROLE_SUPERVISOR":
          this.isSupervisor = true;
          break;
        case "ROLE_MANAGER":
          this.isManager = true;
          break;
        case "ROLE_USER":
          this.isUser = true;
          break;
      }
    });

    if (this.isUser && !this.isAdmin && !this.isManager && !this.isSupervisor) {
      this.loadRoutinesByUser();
    } else {
      this.serverSide = true;
      this.loadRoutines();
    }
  }

  ngAfterViewInit(): void {
    this.sort.sortChange.subscribe((sort: Sort) => this.onSortChange(sort));
  }

  private loadRoutines(): void {
    this.loading = true;
    this.routinesService.findAll(this.currentPage, this.pageSize, this.sortField, this.sortDirection).subscribe({
      next: (response) => {
        if (response) {
          this.dataSource.data = response.content;
          this.totalElements = response.totalElements;
        }
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.errorHandlerService.handle(error, {
          component: "RoutinesListComponent",
          method: "loadRoutines",
          operation: "findAll",
        });
      },
    });
  }

  private loadRoutinesByUser(): void {
    this.loading = true;
    this.routinesService.findAllByMe().subscribe({
      next: (routines) => {
        const list: RoutineResponse[] = Array.isArray(routines) ? routines : [];
        this.dataSource.data = list;
        this.totalElements = list.length;
        this.dataSource.paginator = this.paginator;
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.errorHandlerService.handle(error, {
          component: "RoutinesListComponent",
          method: "loadRoutinesByUser",
          operation: "findAllByMe",
        });
      },
    });
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    if (this.serverSide) {
      this.loadRoutines();
    }
  }

  onSortChange(sort: Sort): void {
    this.sortField = sort.active;
    this.sortDirection = sort.direction || "asc";
    this.currentPage = 0;
    if (this.serverSide) {
      this.loadRoutines();
    }
  }

  applyFilter(event: Event): void {
    const value = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.dataSource.filter = value;
  }

  onEdit(routine: RoutineResponse): void {
    this.router.navigate(["/routines/edit/", routine.id]);
  }

  onDelete(routine: RoutineResponse): void {
    Swal.fire({
      title: `Tem certeza que deseja desativar a rotina "${routine.name}"?`,
      text: "Você não poderá voltar atrás após desativar!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "green",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sim, desativar agora!",
    }).then((result) => {
      if (result.isConfirmed) {
        this.loading = true;
        this.routinesService.delete(routine.id).subscribe({
          next: () => {
            this.toast.success(`A rotina ${routine.name} foi desativada com sucesso.`);
            if (this.serverSide) {
              this.loadRoutines();
            } else {
              this.dataSource.data = this.dataSource.data.filter((r) => r.id !== routine.id);
              this.totalElements = this.dataSource.data.length;
              this.loading = false;
            }
          },
          error: (error) => {
            this.loading = false;
            this.errorHandlerService.handle(error, {
              component: "RoutinesListComponent",
              method: "onDelete",
              operation: "delete",
            });
          },
        });
      }
    });
  }
}
